import { Alert, Box, Text } from '@zillow/constellation-10';

import { DEFAULT_ERROR_MESSAGE } from '@zg-rentals/zillow-rental-manager/constants';

import { useMultiplexOverview } from '../../../../hooks/queries/multiplex';

import { EXIT_CTA_LABEL } from '../../../../constants/WizardConstants';

import CelebrationCard from './components/CelebrationCard';
import UnitItem from './components/UnitItem';

import SpinnerContainer from '../../../SpinnerContainer';
import PageWizard from '../../../Wizard/PageWizard';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const getBedroomDisplay = (value: number | string) => {
  if (value === 0 || value === '0') {
    return 'Studio';
  }

  return value;
};

const formatUnitSummary = (numBedrooms?: number | string, numBathrooms?: number): string | undefined => {
  if (numBedrooms === undefined) {
    return undefined;
  }

  if (numBathrooms === undefined) {
    return undefined;
  }

  const bedroomDisplay = getBedroomDisplay(numBedrooms);

  if (bedroomDisplay === 'Studio') {
    return numBathrooms > 0 ? `Studio, ${numBathrooms}ba` : 'Studio';
  }

  return `${numBedrooms}bd, ${numBathrooms}ba`;
};

const MultiplexOverview = ({
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  ...rest
}: WizardBodyTemplateProps) => {
  const { data, error, isFetching } = useMultiplexOverview();

  const multiplexOverviewErrorMessage = error
    ? (error instanceof Error && error.message.trim()) || DEFAULT_ERROR_MESSAGE
    : null;

  return (
    <PageWizard
      {...rest}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
      onExitTriggered={onExitWizard}
      hideBackButton={true}
      hideFooter={true}
      hideNextButton={true}
      displayPremiumTopOfFunnelHeader={false}
    >
      <Box
        css={{
          margin: '0 auto',
          // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 800px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
          maxWidth: '800px',
          width: '100%',
        }}
        data-testid="MultiplexOverview"
      >
        {error ? (
          <Alert data-testid="MultiplexOverviewError" tone="critical" css={{ marginBlockEnd: '300' }}>
            {multiplexOverviewErrorMessage}
          </Alert>
        ) : (
          <>
            {isFetching && (
              // use Box to offset the spinner to better align with the loadable spinner
              // oxlint-disable-next-line constellation/no-raw-spacing-values -- `bottom: 25px` falls between `600` (24px) and `700` (28px) on the numeric spacing scale
              <Box data-testid="MultiplexOverviewLoading" css={{ position: 'relative', bottom: '25px' }}>
                <SpinnerContainer size="lg" height="100px" />
              </Box>
            )}

            {!isFetching && (
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '400',
                }}
              >
                <Box data-testid="Address" css={{ display: 'flex', flexDirection: 'column' }}>
                  <Text textStyle="heading-sm" data-testid="AddressStreet">
                    {data?.streetAddress ?? ''}
                  </Text>
                </Box>

                <CelebrationCard />

                <Box
                  data-testid="UnitsListContainer"
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: 'stretch',
                    gap: '200',
                  }}
                >
                  <Text textStyle="body-bold" data-testid="UnitsCount">
                    Units ({data?.units?.length ?? 0})
                  </Text>
                  <Box data-testid="UnitsList" css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
                    {(data?.units ?? []).map((unit) => (
                      <UnitItem
                        key={unit.listingAlias}
                        unitName={`Unit ${unit.unitNumber}`}
                        unitType={formatUnitSummary(unit.numBedrooms, unit.numBathrooms)}
                        status={unit.status}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </PageWizard>
  );
};

export default MultiplexOverview;
